import { Typography } from '@mui/material';
import { formatCurrency } from '../../../utils/currency';

export interface MoneyProps {
  /**
   * Null or undefined means the amount is withheld from this viewer, not that
   * it is zero — the API nulls money the viewer has no financial permission
   * for. `showValues` is the separate screen-level toggle.
   */
  value: number | null | undefined;
  /** Theme palette path. Deductions pass 'error.main'. */
  color?: string;
  /** Cancelled amounts are struck through instead of tagged. */
  strike?: boolean;
  /**
   * Quiet treatment for money that is a target rather than a booked value
   * (unbooked items, template band totals).
   */
  quiet?: boolean;
}

/** Stands in for a withheld amount. No em dashes anywhere (decision 14). */
const REDACTED = '··';

/**
 * A ledger amount: tabular figures so columns of money align digit for digit.
 * There is deliberately no "£X of £Y" mode — partial booking shows as a raw
 * percentage next to the title and in full on the hover card.
 */
export const Money = ({
  value,
  color,
  strike = false,
  quiet = false,
}: MoneyProps) => {
  const redacted = value == null;
  const shown = quiet ? 'text.secondary' : 'text.primary';

  return (
    <Typography
      component="span"
      variant={quiet ? 'caption' : 'body2'}
      noWrap
      aria-label={redacted ? 'Value hidden' : undefined}
      sx={{
        fontWeight: quiet ? 400 : 600,
        fontVariantNumeric: 'tabular-nums',
        color: redacted ? 'text.disabled' : (color ?? shown),
        textDecoration: strike && !redacted ? 'line-through' : 'none',
      }}
    >
      {redacted ? REDACTED : formatCurrency(value)}
    </Typography>
  );
};
